/**
 * TCEQ Form 20982 project API client.
 *
 * Deliberately separate from `phreaticApi` (`./phreatic.ts`) so the
 * builder portal Dev 2 is building can import this without dragging
 * the full Phreatic surface along. When the portal lifts the
 * <TceqProjectList /> component, it lifts this client too.
 *
 * Endpoint contract: `src/products/phreatic/cari/reports/tceq_routes.py`.
 */
import { apiClient } from './client';

// ── Types ──────────────────────────────────────────────────────────

export interface TceqProject {
  project_id: string;
  client_id: string;
  site_id: string | null;
  subdivision_name: string;
  proposed_lot_count: number | null;
  tax_book: string | null;
  tax_map: string | null;
  tax_parcel: string | null;
  land_use_purpose: string;
  owner_name: string | null;
  owner_city: string | null;
  owner_state: string | null;
  applicant_name: string | null;
  signing_pe_user_id: string | null;
  form_inputs: Record<string, unknown>;
  overrides: Record<string, unknown>;
  version: number;
  status: 'draft' | 'ready_to_generate' | 'generated';
  created_at: string | null;
  updated_at: string | null;
}

export interface TceqProjectList {
  projects: TceqProject[];
  total: number;
}

export interface TceqProjectCreate {
  subdivision_name: string;
  proposed_lot_count?: number;
  tax_book?: string;
  tax_map?: string;
  tax_parcel?: string;
  site_id?: string;
}

export interface TceqProjectPatch {
  version: number;
  subdivision_name?: string;
  previous_tract_names?: string;
  proposed_lot_count?: number;
  tax_book?: string;
  tax_map?: string;
  tax_parcel?: string;
  land_use_purpose?: string;
  owner_name?: string;
  owner_address_line1?: string;
  owner_city?: string;
  owner_state?: string;
  owner_zip?: string;
  owner_phone?: string;
  owner_email?: string;
  applicant_name?: string;
  applicant_address_line1?: string;
  applicant_city?: string;
  applicant_state?: string;
  applicant_zip?: string;
  applicant_phone?: string;
  applicant_email?: string;
  signing_pe_user_id?: string;
  form_inputs?: Record<string, unknown>;
  overrides?: Record<string, unknown>;
  status?: string;
}

export interface TceqRequiredFields {
  required_missing: string[];
  soft_missing: string[];
}

export interface TceqGenerateResponse {
  report_id: string;
  status: string;
  download_endpoint?: string;
  error?: string;
}

export interface TceqDocument {
  document_id: string;
  original_filename: string;
  document_category: 'lab_report' | 'well_log' | 'supporting';
  file_size: number | null;
  created_at: string | null;
}

export interface TceqIntakeUploadResponse {
  project_id: string;
  version: number;
  fields_updated: number;
  warnings: string[];
}

export interface TceqIntakeUploadErrorBody {
  code:
    | 'stale_template'
    | 'wrong_project'
    | 'missing_required'
    | 'unreadable'
    | 'missing_metadata';
  message: string;
}

// ── Client ─────────────────────────────────────────────────────────

export const tceqApi = {
  listProjects: async (siteId?: string): Promise<TceqProjectList> => {
    const params: Record<string, string> = {};
    if (siteId) params.site_id = siteId;
    const { data } = await apiClient.get('/phreatic/tceq/projects', { params });
    return data;
  },

  createProject: async (body: TceqProjectCreate): Promise<TceqProject> => {
    const { data } = await apiClient.post('/phreatic/tceq/projects', body);
    return data;
  },

  getProject: async (projectId: string): Promise<TceqProject> => {
    const { data } = await apiClient.get(`/phreatic/tceq/projects/${projectId}`);
    return data;
  },

  patchProject: async (
    projectId: string,
    body: TceqProjectPatch,
  ): Promise<TceqProject> => {
    const { data } = await apiClient.patch(
      `/phreatic/tceq/projects/${projectId}`,
      body,
    );
    return data;
  },

  deleteProject: async (projectId: string): Promise<{ status: string }> => {
    const { data } = await apiClient.delete(
      `/phreatic/tceq/projects/${projectId}`,
    );
    return data;
  },

  getRequiredFields: async (projectId: string): Promise<TceqRequiredFields> => {
    const { data } = await apiClient.get(
      `/phreatic/tceq/projects/${projectId}/required`,
    );
    return data;
  },

  generateForm: async (projectId: string): Promise<TceqGenerateResponse> => {
    const { data } = await apiClient.post(
      `/phreatic/tceq/projects/${projectId}/generate`,
    );
    return data;
  },

  uploadDocument: async (
    projectId: string,
    file: File,
    category: TceqDocument['document_category'],
  ): Promise<TceqDocument> => {
    const form = new FormData();
    form.append('file', file);
    form.append('category', category);
    const { data } = await apiClient.post(
      `/phreatic/tceq/projects/${projectId}/documents`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },

  listDocuments: async (projectId: string): Promise<TceqDocument[]> => {
    const { data } = await apiClient.get(
      `/phreatic/tceq/projects/${projectId}/documents`,
    );
    return data;
  },

  /**
   * Download a pre-seeded Excel intake template for the project.
   * Returns the raw .xlsx blob + suggested filename (from
   * Content-Disposition) so the caller triggers a browser save.
   */
  downloadIntakeTemplate: async (
    projectId: string,
  ): Promise<{ blob: Blob; filename: string }> => {
    const res = await apiClient.get(
      `/phreatic/tceq/projects/${projectId}/intake-template`,
      { responseType: 'blob' },
    );
    const disp = (res.headers['content-disposition'] || '') as string;
    const match = /filename="([^"]+)"/.exec(disp);
    const filename = match
      ? match[1]
      : `tceq-20982-intake-${projectId}.xlsx`;
    return { blob: res.data as Blob, filename };
  },

  /**
   * Upload a filled intake template. Server-side errors are
   * structured (`{code, message}`) so the UI can render
   * the right user-facing copy without string-matching.
   */
  uploadIntakeTemplate: async (
    projectId: string,
    file: File,
  ): Promise<TceqIntakeUploadResponse> => {
    const form = new FormData();
    form.append('file', file);
    const { data } = await apiClient.post(
      `/phreatic/tceq/projects/${projectId}/intake-template`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },
};
